package com.zi.support.shared

/**
 * A ticket's optional place: picked on the create form from address suggestions shown while typing (Photon, an
 * OpenStreetMap geocoder that allows search-as-you-type), saved as `location: {address, latitude, longitude}`. The
 * ticket shows the address and a Route button; Route opens [routePage] (a MapLibre map on OpenFreeMap tiles with
 * turn-by-turn directions) in an iframe on the web, a WebView on Android and a WKWebView on iOS, like GpsLocations.
 */
object TicketLocation {
    /** Typing waits this long after the last key before asking for suggestions. */
    const val SUGGEST_DELAY_MS: Long = 350
    /** Suggestions start from this many typed characters. */
    const val MIN_QUERY = 3

    private val leadingNumber = Regex("""^(\d+[A-Za-z]?)[\s,]+(.+)$""")

    /** "18 knights" → "18": the house number typed before the street, which [label] puts back on street suggestions. */
    fun houseNumber(query: String): String = leadingNumber.find(query.trim())?.groupValues?.get(1).orEmpty()

    /**
     * Address suggestions inside South Africa's bounding box. A leading house number is left out of the search: Photon
     * finds nothing for "18 knights" while the street name is half typed, but "knights" finds Knights Road. The reply is GeoJSON: `features[]`, each with
     * `properties` (name, housenumber, street, district, city, state…) and `geometry.coordinates` [longitude, latitude].
     */
    fun searchUrl(query: String): String =
        "https://photon.komoot.io/api/?limit=6&lang=en&bbox=16.3,-35.0,33.0,-22.0&q=" +
            encode(leadingNumber.find(query.trim())?.groupValues?.get(2) ?: query.trim())

    /**
     * One suggestion's address line from its Photon properties ([property] returns "" when missing). [houseNumber]
     * (from [houseNumber] of the typed query) goes in front of a street result that has none: "18 Knights Road, …".
     */
    fun label(houseNumber: String = "", property: (String) -> String): String = listOf(
        property("name").let { name ->
            if (houseNumber.isNotBlank() && name.isNotBlank() && property("housenumber").isBlank() && property("type") == "street") "$houseNumber $name" else name
        },
        listOf(property("housenumber"), property("street")).filter { it.isNotBlank() }.joinToString(" "),
        property("district").ifBlank { property("locality") },
        property("city"),
        property("state"),
    ).map { it.trim() }.filter { it.isNotBlank() }.distinct().joinToString(", ")

    /** Turn-by-turn in Google Maps (app or browser), from wherever the phone is. */
    fun directionsUrl(latitude: Double, longitude: Double): String =
        "https://www.google.com/maps/dir/?api=1&destination=$latitude,$longitude"

    /** The detail view's one-line label: the first three parts of a long address. */
    fun shortAddress(address: String): String = address.split(", ").take(3).joinToString(", ")

    private fun encode(value: String): String = buildString {
        value.encodeToByteArray().forEach { byte ->
            val c = byte.toInt() and 0xff
            if (c.toChar().isLetterOrDigit() && c < 128 || c.toChar() in "-_.~") append(c.toChar())
            else append('%').append("0123456789ABCDEF"[c shr 4]).append("0123456789ABCDEF"[c and 15])
        }
    }

    internal fun jsString(value: String) = "\"" + value.replace("\\", "\\\\").replace("\"", "\\\"").replace("\n", " ").replace("<", "\\u003c") + "\""

    /**
     * The Route screen for one ticket: it asks for the viewer's position, gets the driving route with every turn from the
     * public OSRM server, draws it, and lists the directions ("Turn left onto Joe Slovo Street / Then drive 120 m"); tapping a step
     * shows it on the map. "Open in Google Maps" hands over to live navigation. Without a position it shows the place only.
     * ponytail: OSRM's demo server is for light use; host one (or use a paid router) if it's busy.
     */
    fun routePage(latitude: Double, longitude: Double, address: String): String = ROUTE_PAGE
        .replace("__DEST__", "[$latitude,$longitude]")
        .replace("__ADDRESS__", jsString(address))
        .replace("__DIRECTIONS__", jsString(directionsUrl(latitude, longitude)))

    // No dollar signs below: Kotlin string.
    private const val ROUTE_PAGE = """<!doctype html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1">
<title>Route</title>
<link rel="stylesheet" href="https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css">
<script src="https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js"></script>
<style>
:root{color-scheme:light;--bg:#f4f6f8;--surface:#fff;--text:#17202a;--muted:#748091;--line:#e3e8ee;--brand:#173f5f;--route:#2563eb;--on:#eef4fb}
@media (prefers-color-scheme:dark){:root{color-scheme:dark;--bg:#0e1319;--surface:#151c24;--text:#f1f4f7;--muted:#8f9baa;--line:#293542;--brand:#8fb9d4;--route:#60a5fa;--on:#1c2a38}}
*{box-sizing:border-box}
html,body{margin:0;height:100%;background:var(--bg);color:var(--text);font:14px/1.4 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif}
body{display:flex;flex-direction:column}
.bar{display:flex;align-items:center;gap:10px;padding:12px 14px;background:var(--surface);border-bottom:1px solid var(--line)}
.bar div{flex:1;min-width:0}.bar strong{display:block;font-size:15px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.bar small{color:var(--muted)}
.bar a{flex:none;background:var(--brand);color:var(--surface);text-decoration:none;font-weight:600;padding:9px 13px;border-radius:12px}
#map{flex:none;height:44%;min-height:200px}
.sum{display:none;gap:8px;padding:12px 14px;background:var(--surface);border-top:1px solid var(--line)}
.sum.on{display:flex}
.sum div{flex:1;padding:9px 10px;border-radius:12px;background:var(--bg);text-align:center}
.sum b{display:block;font-size:17px;font-variant-numeric:tabular-nums}.sum span{color:var(--muted);font-size:12px}
#steps{flex:1;min-height:0;overflow:auto;margin:0;padding:4px 0 12px;list-style:none;background:var(--surface)}
#steps li{position:relative;display:flex;gap:12px;align-items:flex-start;padding:12px 14px;cursor:pointer}
#steps li+li:before{content:"";position:absolute;left:31px;top:-12px;height:24px;border-left:2px dotted var(--line)}
#steps li.on{background:var(--on)}
#steps .i{flex:none;width:36px;height:36px;border-radius:12px;background:var(--on);color:var(--route);display:grid;place-items:center;font-size:20px;font-weight:700;position:relative;z-index:1}
#steps li.end .i{background:#dc2626;color:#fff}
#steps .t{flex:1;min-width:0}
#steps .t b{display:block;font-size:15px;font-weight:600;line-height:1.35}
#steps .t small{display:block;margin-top:3px;color:var(--muted);font-size:13px}
#steps .note{color:var(--muted);padding:16px 14px;cursor:default}
</style></head><body>
<div class="bar"><div><strong id="addr"></strong><small id="info">Finding your location…</small></div><a id="go" target="_blank" rel="noopener">Navigate</a></div>
<div id="map"></div>
<div class="sum" id="sum"></div>
<ol id="steps"><li class="note">Directions show here once your location is found.</li></ol>
<script>
var dest = __DEST__, address = __ADDRESS__;
document.getElementById("addr").textContent = address;
document.getElementById("go").href = __DIRECTIONS__;
var info = document.getElementById("info"), list = document.getElementById("steps");
// The bright style in dark mode too: OpenFreeMap's dark style is near-black on a phone.
// Without WebGL (or the map library) the map is hidden and the directions still show.
var map = null, styleReady = new Promise(function () {});
try {
  map = new maplibregl.Map({container: "map", style: "https://tiles.openfreemap.org/styles/liberty", center: [dest[1], dest[0]], zoom: 14, attributionControl: {compact: true}});
  map.addControl(new maplibregl.NavigationControl({showCompass: false}), "top-right");
  new maplibregl.Marker({color: "#dc2626"}).setLngLat([dest[1], dest[0]]).setPopup(new maplibregl.Popup().setText(address)).addTo(map);
  styleReady = new Promise(function (resolve) { map.on("style.load", resolve); });
} catch (e) { map = null; document.getElementById("map").style.display = "none"; }
function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return {"&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;"}[c]; }); }
function minutes(s) { var m = Math.round(s / 60); return m < 60 ? m + " min" : Math.floor(m / 60) + " h " + (m % 60) + " min"; }
function metres(m) { return m >= 1000 ? (m / 1000).toFixed(m >= 10000 ? 0 : 1) + " km" : Math.max(10, Math.round(m / 10) * 10) + " m"; }
function compass(b) { return ["north", "north-east", "east", "south-east", "south", "south-west", "west", "north-west"][Math.round(b / 45) % 8]; }
function ordinal(n) { return n + (n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th"); }
// "slight left" -> "bear left", "sharp right" -> "turn sharp right", "left" -> "turn left".
function turn(mod) { return /slight/.test(mod) ? "bear " + mod.replace("slight ", "") : mod === "straight" ? "go straight" : mod === "uturn" ? "make a U-turn" : "turn " + mod; }
function cap(t) { return t.charAt(0).toUpperCase() + t.slice(1); }
// The road's name with its route number ("Grayston Drive (M85)"), just the number ("M1"), or "" when unnamed.
function roadName(s) {
  var name = s.name || "", ref = (s.ref || "").split(";")[0];
  return name && ref && name.indexOf(ref) < 0 ? name + " (" + ref + ")" : name || ref;
}
// OSRM gives maneuvers, not sentences: this turns each into a plain instruction.
function instruction(s) {
  var m = s.maneuver, mod = m.modifier || "", road = roadName(s);
  // Ramps and forks often have no road name, only where they lead ("M1: Pretoria"); some side streets have neither.
  var onto = road ? " onto " + road : s.destinations ? " towards " + s.destinations : m.type === "arrive" || m.type === "depart" ? "" : " onto an unnamed road";
  var side = /left/.test(mod) ? "left" : /right/.test(mod) ? "right" : "";
  switch (m.type) {
    case "depart": return "Start by heading " + compass(m.bearing_after || 0) + (road ? " on " + road : "");
    case "arrive": return "Arrive at " + address.split(",")[0] + (side ? ", on your " + side : "");
    case "roundabout": case "rotary": return "At " + (s.rotary_name ? "the " + s.rotary_name + " roundabout" : "the roundabout") + ", take the " + ordinal(m.exit || 1) + " exit" + onto;
    case "exit roundabout": case "exit rotary": return "Leave the roundabout" + onto;
    case "merge": return "Merge" + (side ? " " + side : "") + onto;
    case "on ramp": return "Take the on-ramp" + (side ? " on the " + side : "") + onto;
    case "off ramp": return "Take the off-ramp" + (side ? " on the " + side : "") + onto;
    case "fork": return "Keep " + (side || "straight") + " at the fork" + onto;
    case "end of road": return "At the end of the road, " + turn(side || mod) + onto;
    case "continue": return mod === "uturn" ? "Make a U-turn" + onto : /left|right/.test(mod) ? cap(turn(mod)) + (road ? " to stay on " + road : onto) : "Continue straight" + (road ? " on " + road : "");
    default: return cap(turn(mod || "straight")) + onto;
  }
}
// A direction arrow for each step.
function arrow(s) {
  var m = s.maneuver, mod = m.modifier || "";
  if (m.type === "arrive") return "⚑";
  if (m.type === "depart") return "•";
  if (/roundabout|rotary/.test(m.type)) return "↻";
  if (mod === "uturn") return "↶";
  if (/slight left/.test(mod)) return "↖"; if (/slight right/.test(mod)) return "↗";
  if (/left/.test(mod)) return "↰"; if (/right/.test(mod)) return "↱";
  return "↑";
}
// "New name" steps only mean the road changes its name, and zero-length steps are noise: fold them into the step before,
// so each row is a real decision and "then drive …" is the whole stretch to the next one.
function tidy(steps) {
  var out = [];
  steps.forEach(function (s) {
    var last = out[out.length - 1];
    if (last && s.maneuver.type !== "arrive" && (s.maneuver.type === "new name" || (s.distance < 1 && s.maneuver.type === "continue"))) {
      last.distance += s.distance; last.duration += s.duration;
    } else out.push({maneuver: s.maneuver, name: s.name, ref: s.ref, destinations: s.destinations, rotary_name: s.rotary_name, distance: s.distance, duration: s.duration});
  });
  return out;
}
function clock(secondsFromNow) { var d = new Date(Date.now() + secondsFromNow * 1000); return d.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"}); }
function route(from) {
  var dot = document.createElement("div");
  dot.style.cssText = "width:16px;height:16px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)";
  if (map) new maplibregl.Marker({element: dot}).setLngLat([from[1], from[0]]).setPopup(new maplibregl.Popup().setText("You are here")).addTo(map);
  var url = "https://router.project-osrm.org/route/v1/driving/" + from[1] + "," + from[0] + ";" + dest[1] + "," + dest[0] + "?overview=full&geometries=geojson&steps=true";
  fetch(url).then(function (r) { return r.json(); }).then(function (data) {
    var best = data.routes && data.routes[0];
    if (!best) throw new Error("no route");
    styleReady.then(function () {
      map.addSource("route", {type: "geojson", data: {type: "Feature", properties: {}, geometry: best.geometry}});
      map.addLayer({id: "route", type: "line", source: "route", layout: {"line-join": "round", "line-cap": "round"},
        paint: {"line-color": getComputedStyle(document.documentElement).getPropertyValue("--route").trim() || "#2563eb", "line-width": 5, "line-opacity": 0.85}});
    });
    if (map) {
      var b = new maplibregl.LngLatBounds();
      best.geometry.coordinates.forEach(function (c) { b.extend(c); });
      map.fitBounds(b, {padding: 40, duration: 0});
    }
    info.textContent = "Driving directions from where you are";
    var steps = tidy(best.legs[0].steps);
    document.getElementById("sum").innerHTML = "<div><b>" + metres(best.distance) + "</b><span>distance</span></div><div><b>" + minutes(best.duration) +
      "</b><span>by car</span></div><div><b>" + clock(best.duration) + "</b><span>arrive about</span></div>";
    document.getElementById("sum").classList.add("on");
    // Each row: the decision to make there, then how far to drive after it until the next one.
    list.innerHTML = steps.map(function (s, i) {
      var end = s.maneuver.type === "arrive";
      // Name the road on the "then drive" line only when the instruction above didn't.
      var road = roadName(s), text = instruction(s);
      var then = end ? "Your destination" : s.distance > 0 ? "Then drive " + metres(s.distance) + (road && text.indexOf(road) < 0 ? " on " + road : "") + (s.duration >= 60 ? " · " + minutes(s.duration) : "") : "";
      return '<li data-i="' + i + '"' + (end ? ' class="end"' : "") + '><span class="i" aria-hidden="true">' + arrow(s) + '</span><span class="t"><b>' +
        esc(text) + "</b>" + (then ? "<small>" + esc(then) + "</small>" : "") + "</span></li>";
    }).join("");
    list.querySelectorAll("li").forEach(function (li) {
      li.onclick = function () {
        list.querySelectorAll("li.on").forEach(function (x) { x.classList.remove("on"); });
        li.classList.add("on");
        if (map) map.flyTo({center: steps[+li.getAttribute("data-i")].maneuver.location, zoom: 17});
      };
    });
  }).catch(function () {
    if (map) map.fitBounds(new maplibregl.LngLatBounds([from[1], from[0]], [from[1], from[0]]).extend([dest[1], dest[0]]), {padding: 40, duration: 0});
    info.textContent = "Couldn't work out a route; tap Navigate to open Google Maps.";
    list.innerHTML = '<li class="note">No directions available right now.</li>';
  });
}
if (navigator.geolocation) navigator.geolocation.getCurrentPosition(function (p) { route([p.coords.latitude, p.coords.longitude]); },
  function () { info.textContent = "Allow location to get directions from where you are."; list.innerHTML = '<li class="note">Allow location for this app, then tap Route again.</li>'; },
  {enableHighAccuracy: true, timeout: 15000, maximumAge: 60000});
else info.textContent = "This device can't share its location.";
</script></body></html>"""
}
